/**
 * Tiny typed wrapper around fetch for /api/auth/* and (later) other
 * platform-side endpoints.
 *
 * Mirrors admin-ui/src/api/client.ts deliberately - same Promise
 * shape, same ApiError type, same credentials handling - so a
 * dev moving between the two SPAs doesn't have to relearn the
 * call site.
 *
 * Differences from admin-ui:
 *   - No fixed API_BASE prefix. The platform-side surface is
 *     spread across `/api/auth/*` (PR-A), and later `/api/me`
 *     etc. Callers pass the full path.
 *   - No CSRF token plumbing in PR-A. The legacy form-POST
 *     flow it's replacing has none either, so we're not
 *     regressing. CSRF gets added when the dashboard SPA
 *     starts mutating data (PR-D).
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(path, {
    method,
    headers,
    credentials: 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 204) {
    return undefined as T;
  }

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* Non-JSON error body - keep payload null. */
  }

  if (!res.ok) {
    const obj = (payload ?? {}) as { error?: string; code?: string };
    throw new ApiError(
      res.status,
      obj.code ?? 'UNKNOWN',
      obj.error ?? `${method} ${path} failed (${res.status})`,
    );
  }

  return payload as T;
}
